/**
 * A view of a slice, or subarray, of an existing array.
 *
 * Note that no copy is created, so mutations of the original array will be
 * visible through the slice. It's recommended to avoid mutating the sliced
 * part of the array once a slice is created.
 */
export class ArraySlice<T> {
  public readonly length: number;

  /**
   * @param array - The underlying array.
   * @param start - The index at which the slice starts.
   * @param end - The index at which the slice ends. This is non-include - i.e.,
   * array[end] is not part of the slice.
   */
  constructor(
    public readonly array: readonly T[],
    public readonly start: number = 0,
    public readonly end: number = array.length,
  ) {
    this.length = end - start;
  }

  /** Convert the slice into a real array. This requires copying its elements. */
  toArray(): T[] {
    return this.array.slice(this.start, this.end);
  }

  /** Get the item at the provided index relative to the start of the slice. */
  get(index: number): T {
    const absIndex = index + this.start;
    if (absIndex < this.start || absIndex >= this.end) {
      throw new Error(`Index out of bounds`);
    }
    return this.array[absIndex]!;
  }
}
